import {
  ArrowPathIcon,
  PhotoIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { Flex, Image, SimpleGrid, Text } from '@mantine/core';
import type {
  DropzoneProps,
  FileRejection,
  FileWithPath
} from '@mantine/dropzone';
import { Dropzone } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { notificationStyleConfig } from '@/features/notification/config/notificationConfig';
import { resolveImagePath } from '@/shared/helper/resolveImagePath';
import { translate } from '@/shared/i18n/translate';
import { LBActionButton, LBDropZone } from '@/shared/ui-kit/components';

interface LBPhotoDropZoneProps extends Partial<DropzoneProps> {
  initialValue?: string[];
  thumbnailPath?: string;
  onDelete?: () => Promise<void>;
  onFilesChange?: (files: FileWithPath[]) => void;
}

export const LBVideoDropZone = React.memo(
  ({
    initialValue = [],
    children,
    onFilesChange,
    thumbnailPath,
    loading,
    onDelete,
    ...props
  }: LBPhotoDropZoneProps) => {
    const [files, setFiles] = useState<FileWithPath[] | string[]>([]);
    const [showVideo, setShowVideo] = useState(false);
    const [rejectError, setRejectError] = useState<string | null>(null);

    useEffect(() => {
      if (!files.length) {
        setFiles(initialValue);
      }
    }, [initialValue]);

    const [internalLoading, setInternalLoading] = useState(false);
    const openRef = useRef<() => void>(null);

    const handleDrop = (acceptedFiles: FileWithPath[]) => {
      setInternalLoading(true);
      setFiles(acceptedFiles);
      if (onFilesChange) {
        onFilesChange(acceptedFiles);
      }
      setTimeout(() => setInternalLoading(false), 800);
    };
    const handleReject = (rejections: FileRejection[]) => {
      setRejectError(rejections[0].errors[0].message);
      notifications.show({
        title: translate('notifications.rejectedForReason', {
          content: rejections[0].errors[0].message
        }),
        ...notificationStyleConfig
      });
    };

    const getPreviewUrl = (item: string | File) =>
      typeof item === 'string'
        ? resolveImagePath(item)
        : URL.createObjectURL(item);

    const previews = useMemo(
      () =>
        files.map((item, idx) => {
          const imageUrl = getPreviewUrl(item);

          return (
            <div key={`preview-${idx}`}>
              <LBActionButton
                pos='absolute'
                right={20}
                style={{ pointerEvents: 'all', zIndex: 99 }}
                top={20}
                onClick={() => openRef.current?.()}
              >
                <ArrowPathIcon width={20} />
              </LBActionButton>
              <LBActionButton
                left={20}
                pos='absolute'
                style={{ pointerEvents: 'all', zIndex: 99 }}
                top={20}
                onClick={async () => {
                  if (onDelete) {
                    await onDelete();
                    setFiles([]);
                  }
                }}
              >
                <XMarkIcon width={20} />
              </LBActionButton>
              {typeof files[0] === 'string' && (
                <>
                  {!showVideo && (
                    <Image
                      alt={`preview-${idx}`}
                      src={resolveImagePath(thumbnailPath)}
                      style={{ pointerEvents: 'all' }}
                      onClick={() => {
                        setShowVideo(true);
                      }}
                    />
                  )}
                  {showVideo && (
                    <video
                      autoPlay
                      muted
                      controls={!!imageUrl}
                      preload='metadata'
                      style={{ pointerEvents: 'all' }}
                      width='100%'
                    >
                      <source src={`${imageUrl}#1.0`} type='video/mp4' />
                    </video>
                  )}
                </>
              )}
              {typeof files[0] !== 'string' && (
                <video
                  muted
                  controls={!!imageUrl}
                  src={imageUrl}
                  style={{ pointerEvents: 'all' }}
                  width='100%'
                />
              )}
            </div>
          );
        }),
      [files]
    );

    return (
      <LBDropZone
        {...props}
        accept={['video/mp4', 'video/mpeg', 'video/quicktime']}
        activateOnClick={!files.length}
        loading={internalLoading}
        maxFiles={1}
        maxSize={Number(import.meta.env.VITE_VIDEO_MAX_SIZE) * 1024 ** 2}
        openRef={openRef}
        pos='relative'
        onDrop={handleDrop}
        onReject={handleReject}
      >
        <Dropzone.Reject>
          <Flex align='center' direction='column' py={70}>
            <PhotoIcon color='red' width={64} />
            <Text c='red' fz='sm'>
              {rejectError}
            </Text>
          </Flex>
        </Dropzone.Reject>
        {files.length > 1 ? (
          <SimpleGrid cols={3}>{previews}</SimpleGrid>
        ) : files.length ? (
          <div>{previews}</div>
        ) : (
          <Dropzone.Idle>{children}</Dropzone.Idle>
        )}
      </LBDropZone>
    );
  }
);
